/**
 * /multiplication-table — таблица умножения: таблица, «учить», тренажёр,
 * проверка с грамотой, печать и режим для класса на одной странице.
 *
 * Этот файл — только сборка: вкладки, ссылка «поделиться» (состояние в
 * hash) и общий запуск. Движок без DOM — facts / progress / quiz / sheets /
 * notation, разделы — ui-*.js.
 *
 * Ссылка несёт вкладку и её настройки: учитель раздаёт «#tab=train&t=6.7.8»,
 * и у каждого ученика открывается тренажёр на эти таблицы; «#tab=print&…&seed=»
 * пересобирает тот же лист примеров.
 */

import { ctx, $, on, emit, ae, stage, refreshSignLabels, markOne, localizeStatic, rerenderTableChips } from './ctx.js';
import { initChart, applyChartState, chartState, setSize } from './ui-chart.js';
import { initLearn, applyLearnState } from './ui-learn.js';
import { initTrain, applyTrainState, trainState, startTraining } from './ui-train.js';
import { initPrint, applyPrintState, printState } from './ui-print.js';
import { initScreen } from './ui-screen.js';

const TABS = ['table', 'learn', 'train', 'exam', 'print', 'screen'];
let current = 'table';
let ready = false;

function setTab(name, opts) {
    if (TABS.indexOf(name) < 0) name = 'table';
    const o = opts || {};
    const apply = () => {
        current = name;
        TABS.forEach((tb) => {
            const btn = $('mtTab-' + tb);
            const on = tb === name;
            btn.classList.toggle('on', on);
            btn.setAttribute('aria-selected', on ? 'true' : 'false');
            btn.setAttribute('tabindex', on ? '0' : '-1');
            $('mtPane-' + tb).hidden = !on;
        });
        emit('tab', name);
    };
    if (o.animate && window.__viewTransition) window.__viewTransition(apply); else apply();
    if (!o.silent) {
        saveHash();
        ae('tab', { tab: name });
        if (name !== 'table') stage('interact');
    }
}

/* ── ссылка ──────────────────────────────────────────────────────────── */
function stateFor(tab, extra) {
    const q = { tab };
    if (tab === 'table') {
        const c = chartState();
        q.sz = ctx.size;
        if (c.pat !== 'none') q.pat = c.pat;
        if (c.pat === 'multiples') q.k = c.k;
        if (c.view !== 'grid') q.view = c.view;
    } else if (tab === 'train') {
        Object.assign(q, trainState(), { sz: ctx.size });
    } else if (tab === 'print') {
        Object.assign(q, printState());
    } else {
        q.sz = ctx.size;
    }
    return Object.assign(q, extra || {});
}

function saveHash() {
    const p = new URLSearchParams(stateFor(current));
    try { history.replaceState(null, '', location.pathname + location.search + '#' + p.toString()); } catch (_) { /* песочница */ }
}

export function shareLink(tab, extra) {
    const p = new URLSearchParams(stateFor(tab, extra));
    const url = new URL(location.href);
    url.searchParams.delete('nocache');
    url.hash = p.toString();
    return url.toString();
}

function loadHash() {
    const h = String(location.hash || '').replace(/^#/, '');
    if (!h) return false;
    const p = new URLSearchParams(h);
    const q = {};
    p.forEach((v, k) => { q[k] = v; });
    if ([9, 10, 12, 20].indexOf(+q.sz) >= 0 && +q.sz !== ctx.size) setSize(+q.sz);
    const tab = TABS.indexOf(q.tab) >= 0 ? q.tab : 'table';
    setTab(tab, { silent: true });
    if (tab === 'table') applyChartState(q);
    if (tab === 'learn') applyLearnState(q);
    if (tab === 'train') applyTrainState(q);
    if (tab === 'print') applyPrintState(q);
    ae('open_link', { tab });
    return true;
}

/* ── запуск ──────────────────────────────────────────────────────────── */
function init() {
    initChart();
    initLearn();
    initTrain();
    initPrint(shareLink);
    initScreen(shareLink);
    refreshSignLabels();
    // Последним среди слушателей: разделы уже перерисовали своё, остаются подписи сервера.
    on('notation', () => { refreshSignLabels(); rerenderTableChips(); localizeStatic($('mtApp')); saveHash(); });
    on('tab', (name) => { if (name !== current) setTab(name, { animate: false }); });

    const list = document.querySelector('.mt-tabs');
    list.addEventListener('click', (e) => {
        const b = e.target.closest && e.target.closest('[data-mt-tab]');
        if (b) setTab(b.getAttribute('data-mt-tab'), { animate: true });
    });
    // Стрелки по вкладкам — как у настоящего tablist.
    list.addEventListener('keydown', (e) => {
        const i = TABS.indexOf(current);
        const rtl = getComputedStyle(list).direction === 'rtl';
        let j = -1;
        if (e.key === 'ArrowRight') j = rtl ? i - 1 : i + 1;
        if (e.key === 'ArrowLeft') j = rtl ? i + 1 : i - 1;
        if (e.key === 'Home') j = 0;
        if (e.key === 'End') j = TABS.length - 1;
        if (j < 0 && e.key !== 'Home') return;
        e.preventDefault();
        j = (j + TABS.length) % TABS.length;
        setTab(TABS[j]);
        $('mtTab-' + TABS[j]).focus();
    });
    on('size', saveHash);
    on('state', () => { if (ready) saveHash(); });

    if (!loadHash()) setTab('table', { silent: true });
    localizeStatic($('mtApp'));
    ready = true;
    window.__mtApi = { setTab, shareLink, startTraining, markOne };
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
